import * as Sentry from "@sentry/vue"
import { watch } from "vue"
import router from "../router"
import { isImmersive } from "@/composables/useImmersiveMode.ts"
import i18n from "./i18n/i18n"
import { addFeedbackContext, trackActivity } from "./sentryFeedbackContext"
import { addPrioritySelect, tagPriority } from "./sentryFeedbackPriority"
import { addAttachmentPicker, attachFiles } from "./sentryFeedbackAttachments"
import { abandonSending, addConfirmationStyle, beginSending, confirmSent } from "./sentryFeedbackConfirmation"

export default app => {
  Sentry.init({
    app,
    // A release identifies the BUILD, so it travels with the image.
    release: import.meta.env.VITE_VERSION,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    // An environment identifies the DEPLOYMENT, so it must not: a compiled-in value made a dev-*
    // image report `development` in production. Written at container start by
    // docker/40-lewara-runtime-config.sh; the fallback is for `npm run dev`.
    environment: window.__LEWARA_CONFIG__?.environment || import.meta.env.VITE_ENVIRONMENT || "development",
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        // The line is OUR CHROME vs THEIR DATA, not "text" vs "no text". Everything unmasked below
        // renders a translated constant or an asset of ours, so showing it costs no privacy and is
        // what makes a replay legible at all; anything rendered from an API response stays masked by
        // the defaults. rrweb resolves nearest-ancestor-first, so `sentry-mask` on a child re-masks
        // it inside an unmasked container — the escape hatch for when one of these selectors happens
        // to wrap data after all.
        maskAllText: true,
        unmask: [
          // Opt in at the call site for anything the policy below does not reach (LewNavigationBar).
          ".sentry-unmask",
          // What was clicked, and what the pointer was told it would do.
          ".v-btn",
          ".v-tab",
          ".v-tooltip",
          // Form chrome. Labels and validation messages are ours (services/validation.js is entirely
          // i18n constants); the typed values are not, and maskAllInputs below keeps those masked
          // whatever is unmasked here.
          ".v-label",
          ".v-field-label",
          ".v-messages__message",
          // Column headers, empty states, inline alerts — translated constants throughout.
          ".v-data-table__th",
          ".v-empty-state",
          ".v-alert__content",
          // The snackbars, error ones above all: a replay of a failure whose red bar reads as a row
          // of asterisks is missing the one frame it exists for. The wording is always ours
          // (services/api.js#messageForError translates a violationCode and hands on nothing the
          // server wrote), and this is what puts the trace id on screen next to the click that
          // earned it. The narrow cost: a few violations interpolate one value — DUPLICATE_NAME the
          // duplicated name, the invoice-generator ones a position/material/machine name. Those
          // become readable; nothing else does.
          ".v-snackbar__content",
        ],
        // Typed values stay masked whatever is unmasked above.
        maskAllInputs: true,
        // Blocked by default because most images here are the customer's: LewAvatar photos, the
        // tenant logo, Leaflet's tiles (which give away where a site is), the cost chart. Our own
        // marks are not, and a replay with the brand and the toolbar glyphs missing reads as a
        // broken page rather than the app.
        blockAllMedia: true,
        unblock: [
          // Opt in at the call site, mirroring `sentry-unmask`.
          ".sentry-unblock",
          // LewLogo — our wordmark and diamond, from src/assets. The tenant's uploaded logo is a
          // different component (v-img in LewNavigationBar / LewTenantLogoInput) and stays blocked.
          ".lew-logo",
          // Inline glyphs in the personnel-planning gantt: <path> data of ours, nothing rendered
          // from a response. Named one by one so a future data-bearing <svg> is blocked by default —
          // LewProjectCostLineChart, which plots a customer's costs, is exactly that.
          "svg.menu-icon",
          "svg.team-icon",
          "svg.person-icon",
          "svg.child-icon",
          "svg.connector-icon",
        ],
      }),
      Sentry.feedbackIntegration({
        // The round button is created below instead, where it can be held on to.
        autoInject: false,
        colorScheme: "light",
        showBranding: false,
        showName: false,
        showEmail: false,
        triggerLabel: "",
        formTitle: i18n.global.t("general.sentry.formTitle"),
        isRequiredLabel: i18n.global.t("general.sentry.isRequiredLabel"),
        submitButtonLabel: i18n.global.t("general.sentry.submitButtonLabel"),
        cancelButtonLabel: i18n.global.t("general.sentry.cancelButtonLabel"),
        confirmButtonLabel: i18n.global.t("general.sentry.confirmButtonLabel"),
        addScreenshotButtonLabel: i18n.global.t("general.sentry.addScreenshotButtonLabel"),
        removeScreenshotButtonLabel: i18n.global.t("general.sentry.removeScreenshotButtonLabel"),
        messageLabel: i18n.global.t("general.sentry.messageLabel"),
        messagePlaceholder: i18n.global.t("general.sentry.messagePlaceholder"),
        // Hidden in favour of LewFeedbackSentDialog; shown only if the SDK's markup changes under it.
        successMessageText: i18n.global.t("general.sentry.successMessageText"),
        highlightToolText: i18n.global.t("general.sentry.highlightToolText"),
        hideToolText: i18n.global.t("general.sentry.hideToolText"),
        removeHighlightText: i18n.global.t("general.sentry.removeHighlightText"),
        errorGenericText: i18n.global.t("general.sentry.errorGenericText"),
        errorTimeoutText: i18n.global.t("general.sentry.errorTimeoutText"),
        errorForbiddenText: i18n.global.t("general.sentry.errorForbiddenText"),
        onFormOpen: () => {
          addPrioritySelect()
          addAttachmentPicker()
          addConfirmationStyle()
        },
        // Here rather than in LewToolBarHelp's createForm, so the round button's form confirms too.
        onSubmitSuccess: confirmSent,
        onSubmitError: abandonSending,
      }),
    ],
    // Known-benign errors we never want to be alerted on.
    // - "Map container not found.": a @vue-leaflet teardown race — the library builds
    //   the map in an async onMounted and, if the host unmounts while its dynamic
    //   imports are in flight, calls L.map() on an already-detached container. The map
    //   is being destroyed anyway, so it is not actionable (see plugins/leaflet.js,
    //   which warms those chunks to make this rare in the first place).
    // - Dynamic-import / module-load failures: fired when a user on a tab from a
    //   previous release requests a content-hashed route chunk whose filename changed
    //   after a new production deploy. router.onError (see router/index.js) already
    //   hard-reloads onto the fresh index.html, so this is expected and self-healing,
    //   not actionable. Patterns mirror the browser-specific variants handled there
    //   (Chrome / Firefox / Safari / Vite CSS preload).
    ignoreErrors: [
      "Map container not found.",
      "Failed to fetch dynamically imported module",
      "error loading dynamically imported module",
      "Importing a module script failed",
      "Unable to preload CSS for",
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^\/api/],
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Drop 401s — session expiry is expected and handled by the app, not a bug
    beforeSend(event, hint) {
      const error = hint?.originalException
      if (error?.response?.status === 401) return null
      return event
    },
  })

  const client = Sentry.getClient()
  if (client) {
    client.on("beforeSendFeedback", tagPriority)
    // Files picked in the form, next to the SDK's screenshot (see sentryFeedbackAttachments.js).
    client.on("beforeSendFeedback", attachFiles)
    // Tags the Vorgangsnummer and opens the dialog that follows the report out (see
    // sentryFeedbackConfirmation.js).
    client.on("beforeSendFeedback", beginSending)
    // What the person did and had open before writing (see sentryFeedbackContext.js).
    trackActivity(client)
    client.on("beforeSendFeedback", addFeedbackContext)
  }

  // While a view has taken over the screen (the enlarged personnel planning), the round button steps
  // aside with the app bar: pinned to the bottom-right corner, it lay on the controls of the lowest
  // section's bar. `hide()` is the SDK's own, the one it uses while its form is open.
  const feedbackButton = Sentry.getFeedback()?.createWidget()
  if (feedbackButton) {
    watch(isImmersive, immersive => (immersive ? feedbackButton.hide() : feedbackButton.show()))
  }

  // Catch promise rejections outside Vue's error handler (e.g. in plain JS modules, setTimeout)
  window.addEventListener("unhandledrejection", event => {
    Sentry.captureException(event.reason)
  })
}
